import React, { createContext, Dispatch, SetStateAction, useCallback, useReducer, useState } from 'react';
import { SdnOperationTaskStatus, SdnOperationType } from '../services/sdn';

export interface UnitUpload {
    bucket: string,
    name: string,
    progress: number | null,
    finished?: boolean,
    abortController?: AbortController,
    size: number,
    started?: Date,
    restore?: { uuid: string, bucket: string }
}

export type UnitActions = 'added' | 'changed' | 'deleted';

function uploadReducer(state: UnitUpload[], action: UnitUpload & { type: UnitActions }) {
    switch (action.type) {
        case 'added': {
            if (state.some(t => t.name === action.name && t.bucket === action.bucket)) {
                return [...state];
            } else {
                return [...state, {
                    name: action.name,
                    bucket: action.bucket,
                    progress: action.progress,
                    finished: action.finished,
                    started: action.started,
                    abortController: action.abortController,
                    size: action.size,
                    restore: action.restore,
                }];
            }
        }
        case 'changed': {
            return state.map(t => {
                if (t.name === action.name && t.bucket === action.bucket) {
                    return { ...t, ...action };
                } else {
                    return t;
                }
            });
        }
        case 'deleted': {
            return state.filter(t => !(t.name === action.name && t.bucket === action.bucket));
        }
        default: {
            throw Error('Unknown action: ' + action.type);
        }
    }
}

export interface OperationUnit {
    operation: SdnOperationType,
    id: string,
    status: SdnOperationTaskStatus,
    name: string,
    progress: number | null,
    src: string | string[],
    dst: string,
    time?: Date | string,
    size?: number,
}

function operationReducer(state: OperationUnit[], action: OperationUnit & { type: UnitActions }) {
    switch (action.type) {
        case 'added': {
            const contains = !!state.find(t => t.id === action.id);

            if (!contains) {
                return [...state.filter(t => !(t.id === action.id)), {
                    id: action.id,
                    name: action.name,
                    status: action.status,
                    operation: action.operation,
                    progress: action.progress,
                    src: action.src,
                    dst: action.dst,
                    time: new Date(),
                    size: action.size || 0,
                }];
            } else {
                return state;
            }
        }
        case 'changed': {
            const contains = !!state.find(t => t.id === action.id);
            if (!contains) {
                return state;
                // return [...state, action];
            }

            return state.map(t => {
                if (t.id === action.id) {
                    return { ...t, ...action };
                } else {
                    return t;
                }
            });
        }
        case 'deleted': {
            return state.filter(t => !(t.id === action.id));
        }
        default: {
            throw Error('Unknown action: ' + action.type);
        }
    }
}

class UploadProcesser {
}

export interface UploadFilesContextType {
    uploadFiles: { [key: string]: UnitUpload }
    setUploadingFiles: Dispatch<SetStateAction<{ [key: string]: UnitUpload }>>
    uploadIsRunning: boolean
    setUploadIsRunning: (v: boolean) => void,
    processers: UploadProcesser[],
    state: UnitUpload[],
    dispatch: React.Dispatch<UnitUpload & { type: UnitActions }>,
    operations: OperationUnit[],
    dispatchOperations: React.Dispatch<Omit<OperationUnit, 'time'> & { type: UnitActions }>,
}

const defaultContext: UploadFilesContextType = {
    uploadFiles: {},
    state: [],
    dispatch: () => { return },
    operations: [],
    dispatchOperations: () => { return },
    setUploadingFiles: () => ({}),
    uploadIsRunning: false,
    processers: [],
    setUploadIsRunning: () => { return }
}

export const UploadFilesContext = createContext<UploadFilesContextType>(defaultContext);

interface UploadContextProviderProps {
    children?: React.ReactNode,
}

function UploadContextProvider({ children }: UploadContextProviderProps) {
    const [uploadFiles, setUploadingFiles] = useState<{ [key: string]: UnitUpload }>({});
    const [uploadIsRunning, setUploadIsRunning] = useState(false);
    const [processers, setProcessers] = useState<UploadProcesser[]>([]);
    const [state, dispatch] = useReducer(uploadReducer, []);
    const [operations, dispatchOperations] = useReducer(operationReducer, []);

    const appendProcesser = useCallback(() => {
        const processer = new UploadProcesser();
        setProcessers((prev) => [...prev, processer]);
    }, [setProcessers]);

    // Learn more in http://kcd.im/optimize-context
    const value = { uploadFiles, setUploadingFiles, uploadIsRunning, setUploadIsRunning, processers, appendProcesser, state, dispatch, operations, dispatchOperations }
    return <UploadFilesContext.Provider value={value}>{children}</UploadFilesContext.Provider>
}

export default UploadContextProvider